import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import {
  INTERNAL_LIAISON,
  INTERNAL_LIAISON_EMAIL,
  NGO_CONTACT_EMAIL,
  NGO_CONTACT_NAME,
  NGO_NAME,
  RELEVANCE_TO_COMPANY,
  REPRESENTATION_CATEGORY,
} from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCreateMaterialitySHNgosMutation, useUpdateMaterialitySHNgosMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import FormNGO from './components/FormNGO';
import useCreateNGOForm from './hooks/useCreateNGOForm';

import type IMaterialSHNgosItem from 'store/slices/materialAssessment/interfaces/IMaterialSHNgosItem';
import type { TNGOCreateSchema } from 'utils/validators';
import type ICreateNGO from './interfaces/ICreateNGO';

import styles from './scss/CreateNGO.module.scss';

function CreateNGO({
  open, onClose, title, selectedItem, recordId, currentId, setOpenModal, setItemNGO,
  vcmId,
}: ICreateNGO) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createEmployees] = useCreateMaterialitySHNgosMutation();
  const [updateEmployees] = useUpdateMaterialitySHNgosMutation();
  const { setFieldsErrors } = useFieldsErrors();
  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
  } = useCreateNGOForm({ isPageLoading: isLoading, selectedItem });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddNGO = (item: IMaterialSHNgosItem) => {
    if (currentId !== -1) {
      updateEmployees({
        id: currentId, maId: recordId, data: item, vcmId,
      }).then((request) => {
        toast.success(i18n.t('notifications.'));
        if ('data' in request && request.data?.data) {
          setItemNGO((prev) => {
            const index = prev.findIndex((itemIndex) => itemIndex.id === currentId);
            if (index !== -1) {
              const updatedNgos = [...prev];
              updatedNgos[index] = { ...updatedNgos[index], ...request.data.data };
              return updatedNgos;
            }
            return prev;
          });
        }
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TNGOCreateSchema>({ errors, setError, fieldsName });
      });
    } else {
      createEmployees({
        data: item,
        maId: recordId,
        vcmId,
      }).then(() => {
        toast.success(i18n.t('notifications.createdNGO'));
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TNGOCreateSchema>({ errors, setError, fieldsName });
      });
    }
  };

  const onCreateNGOSubmit = handleSubmit((data) => {
    onAddNGO({
      name: data[NGO_NAME],
      representationCategory: data[REPRESENTATION_CATEGORY]?.value || '',
      relevance: data[RELEVANCE_TO_COMPANY] || '',
      contactName: data[NGO_CONTACT_NAME] || '',
      contactEmail: data[NGO_CONTACT_EMAIL] || '',
      internalLiaison: data[INTERNAL_LIAISON] || '',
      internalLiaisonEmail: data[INTERNAL_LIAISON_EMAIL] || '',
    });
    onCancel();
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <FormNGO control={control} />
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateNGOSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateNGO;
